import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  ClipboardDocumentIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import type { AnfrageMailContentDetail } from "@/lib/anfragen";
import { htmlToPlainText } from "@/lib/mailHtml";
import { toast } from "@/lib/toast";
import { fmtMailDate, mailReadDocumentHtml, plainTextToSafeMailHtml } from "@/pages/anfragen/anfrageWizardUtils";

function detailPlainText(detail: AnfrageMailContentDetail | null): string {
  if (!detail?.body) return "";
  return detail.body_content_type === "html" ? htmlToPlainText(detail.body) : detail.body;
}

export function MailReadDialog({
  open,
  detail,
  onClose,
}: {
  open: boolean;
  detail: AnfrageMailContentDetail | null;
  onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [usePlain, setUsePlain] = useState(false);
  const blobRef = useRef<string | null>(null);

  useEffect(() => {
    if (blobRef.current) {
      try {
        URL.revokeObjectURL(blobRef.current);
      } catch {
        /* ignore */
      }
      blobRef.current = null;
    }
    setBlobUrl(null);
    setUsePlain(false);

    if (!open || !detail) return;

    const raw = (detail.body || "").trim();
    const asHtml = detail.body_content_type === "html" && raw;
    if (!raw) {
      setUsePlain(true);
      return;
    }

    const inner = asHtml ? detail.body || "" : plainTextToSafeMailHtml(detail.body || "");
    const doc = mailReadDocumentHtml(inner);
    const blob = new Blob([doc], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    blobRef.current = url;
    setBlobUrl(url);

    return () => {
      if (blobRef.current) {
        try {
          URL.revokeObjectURL(blobRef.current);
        } catch {
          /* ignore */
        }
        blobRef.current = null;
      }
    };
  }, [open, detail]);

  const isSent = detail?.kind === "sent" || (!detail?.kind && !detail?.from_label && !!detail?.to_label);
  const isOriginal = !!detail?.via_graph;

  async function copyText() {
    const text = detailPlainText(detail).trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Text kopiert.");
    } catch {
      toast.error("Kopieren nicht möglich.");
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      zIndexClass="z-[120]"
      panelClassName="max-h-[92vh]"
      bodyClassName="!p-0"
      title={
        <span className="flex flex-wrap items-center gap-2">
          <span
            className={clsx(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide",
              isSent ? "bg-accent-soft text-accent" : "bg-status-green/15 text-status-green",
            )}
          >
            {isSent ? (
              <ArrowUpRightIcon className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <ArrowDownLeftIcon className="h-3.5 w-3.5" aria-hidden />
            )}
            {isSent ? "Gesendete Anfrage" : "Antwort"}
          </span>
          {isOriginal ? (
            <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
              Original aus Microsoft 365
            </span>
          ) : null}
        </span>
      }
      footer={
        <footer className="flex w-full items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void copyText()}
            disabled={!detailPlainText(detail).trim()}
            leftIcon={<ClipboardDocumentIcon className="h-4 w-4" aria-hidden />}
          >
            Text kopieren
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        </footer>
      }
    >
      <div className="flex max-h-[calc(92vh-9rem)] min-h-0 flex-col">
        <header className="shrink-0 border-b border-separator bg-card px-5 py-3">
          <h3 className="text-base font-semibold text-label">{detail?.subject || "(ohne Betreff)"}</h3>
          <dl className="mt-1.5 grid gap-x-4 gap-y-0.5 text-sm sm:grid-cols-[auto_1fr]">
            {detail?.from_label ? (
              <>
                <dt className="text-label-tertiary">Von</dt>
                <dd className="truncate text-label-secondary">{detail.from_label}</dd>
              </>
            ) : null}
            {detail?.to_label ? (
              <>
                <dt className="text-label-tertiary">An</dt>
                <dd className="truncate text-label-secondary">{detail.to_label}</dd>
              </>
            ) : null}
            {detail?.date ? (
              <>
                <dt className="text-label-tertiary">{isSent ? "Gesendet" : "Empfangen"}</dt>
                <dd className="text-label-secondary">{fmtMailDate(detail.date)}</dd>
              </>
            ) : null}
          </dl>
        </header>

        {detail?.note ? (
          <div className="flex shrink-0 items-start gap-2 border-b border-separator bg-fill-quaternary/40 px-5 py-2.5 text-xs text-label-secondary">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-label-tertiary" aria-hidden />
            <p>{detail.note}</p>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-hidden bg-fill-quaternary/30 p-3 sm:p-4">
          {usePlain || !blobUrl ? (
            <pre className="h-[min(60vh,560px)] overflow-auto whitespace-pre-wrap break-words rounded-ios border border-separator bg-card p-4 font-sans text-sm text-label">
              {detail?.body?.trim() || "Kein Inhalt."}
            </pre>
          ) : (
            <iframe
              title="Mailinhalt"
              sandbox=""
              src={blobUrl}
              className="h-[min(60vh,560px)] w-full rounded-ios border border-separator bg-white"
            />
          )}
        </div>
      </div>
    </Dialog>
  );
}
